import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Pencil, Trash2, Mail, Phone, Building2, User, Linkedin, Calendar, Target, Heart, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import ContactDialog from '../components/ContactDialog';
import DeleteDialog from '../components/DeleteDialog';
import HistorySection from '../components/HistorySection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'estrategico', label: 'Estratégico — 15 días', days: 15 },
  { value: 'prioritario', label: 'Prioritario — 30 días', days: 30 },
  { value: 'activo', label: 'Activo — 60 días', days: 60 },
  { value: 'orbita', label: 'En órbita — 90 días', days: 90 },
  { value: 'no_seguir', label: 'No seguir', days: null },
];

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, currentUser } = useUser();
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);
  const [activities, setActivities] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingFrequency, setSavingFrequency] = useState(false);

  useEffect(() => {
    fetchContactData();
  }, [id]);

  const getFullName = (contact) => {
    if (contact.first_name || contact.last_name) {
      return `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    }
    return contact.name || 'Sin nombre';
  };

  const fetchContactData = async () => {
    try {
      const contactRes = await axios.get(`${API}/contacts/${id}`);
      setContact(contactRes.data);

      // Fetch related data
      const [activitiesRes, oppsRes] = await Promise.all([
        axios.get(`${API}/activities?contact_id=${id}`),
        axios.get(`${API}/opportunities`)
      ]);
      
      setActivities(activitiesRes.data);
      
      // Filter opportunities that might be related to this contact's company
      if (contactRes.data.company_id) {
        const companyRes = await axios.get(`${API}/companies/${contactRes.data.company_id}`);
        setCompany(companyRes.data);
        setOpportunities(oppsRes.data.filter(o => o.company_id === contactRes.data.company_id));
      }
    } catch (error) {
      console.error('Error fetching contact data:', error);
      toast.error('Error al cargar el contacto');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contacto eliminado correctamente');
      navigate('/contacts');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Error al eliminar el contacto');
    }
  };

  const handleFrequencyChange = async (value) => {
    if (!isEditor) return;
    const newValue = value === '__none__' ? null : value;
    setSavingFrequency(true);
    try {
      await axios.patch(
        `${API}/contacts/${id}`,
        { cultivation_frequency: newValue },
        { headers: { 'X-User-Email': currentUser?.email } }
      );
      setContact((prev) => ({ ...prev, cultivation_frequency: newValue }));
      toast.success('Frecuencia de seguimiento actualizada');
    } catch (error) {
      console.error('Error updating frequency:', error);
      toast.error('Error al actualizar la frecuencia');
    } finally {
      setSavingFrequency(false);
    }
  };

  const getActivityTypeColor = (type) => {
    const colors = {
      meeting: 'bg-blue-100 text-blue-800',
      call: 'bg-green-100 text-green-800',
      email: 'bg-purple-100 text-purple-800',
      reflexion: 'bg-amber-100 text-amber-800',
      evento: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-sm text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-sm text-muted-foreground">Contacto no encontrado</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="contact-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/contacts')}
        className="mb-4"
        data-testid="back-to-contacts-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver a Contactos
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="contact-name">
              {getFullName(contact)}
            </h1>
            {contact.role && (
              <p className="text-muted-foreground mt-1">{contact.role}</p>
            )}
            {contact.owner && (
              <Badge variant="outline" className="mt-2">
                Responsable: {contact.owner}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {isEditor && (
            <Button variant="outline" onClick={() => setEditDialogOpen(true)} data-testid="edit-contact-btn">
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </Button>
          )}
          {isEditor && (
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-contact-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-medium tracking-tight">Información de Contacto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {contact.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Email</div>
                    <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Teléfono</div>
                    <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                      {contact.phone}
                    </a>
                  </div>
                </div>
              )}
              {contact.linkedin && (
                <div className="flex items-center gap-3">
                  <Linkedin className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">LinkedIn</div>
                    <a 
                      href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Ver perfil
                    </a>
                  </div>
                </div>
              )}
              {company && (
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Empresa</div>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-primary"
                      onClick={() => navigate(`/companies/${company.id}`)}
                    >
                      {company.name}
                    </Button>
                  </div>
                </div>
              )}

              {/* Days since last contact/activity */}
              <div className="flex items-start gap-3 pt-3 border-t" data-testid="last-contact-block">
                <Clock className="w-5 h-5 text-slate-500 mt-1" />
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5">
                    Último contacto o actividad
                  </div>
                  <div className={`text-sm font-medium ${
                    contact.days_since_last_contact == null
                      ? 'text-muted-foreground'
                      : contact.days_since_last_contact >= 60
                      ? 'text-red-600'
                      : contact.days_since_last_contact >= 30
                      ? 'text-orange-600'
                      : 'text-foreground'
                  }`}>
                    {contact.days_since_last_contact == null
                      ? 'Sin contacto registrado'
                      : contact.days_since_last_contact === 0
                      ? 'Contactado hoy'
                      : `Hace ${contact.days_since_last_contact} día${contact.days_since_last_contact === 1 ? '' : 's'}`}
                  </div>
                  {contact.last_contact_at && (
                    <div className="text-xs text-muted-foreground">
                      {new Date(contact.last_contact_at).toLocaleDateString('es-ES', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Cultivation Frequency - quick dropdown */}
              <div className="flex items-start gap-3 pt-3 border-t" data-testid="cultivation-frequency-block">
                <Heart className="w-5 h-5 text-pink-500 mt-1.5" />
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Frecuencia de seguimiento
                  </div>
                  <Select
                    value={contact.cultivation_frequency || '__none__'}
                    onValueChange={handleFrequencyChange}
                    disabled={!isEditor || savingFrequency}
                  >
                    <SelectTrigger className="w-full sm:w-[260px]" data-testid="cultivation-frequency-select">
                      <SelectValue placeholder="Sin configurar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin configurar</SelectItem>
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Determina cada cuántos días este contacto aparece en "Relaciones que Cultivar".
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Relationship History Section */}
          <HistorySection 
            entityType="contacts" 
            entityId={id} 
            historyType="relationship" 
          />

          {/* Recent Activities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-medium tracking-tight">
                Actividades Recientes ({activities.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No hay actividades registradas</p>
              ) : (
                <div className="space-y-3">
                  {activities.slice(0, 5).map((activity) => (
                    <div 
                      key={activity.id} 
                      className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => navigate('/activities')}
                    >
                      <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{activity.title || activity.type}</span>
                          <Badge className={`${getActivityTypeColor(activity.type)} text-xs`}>
                            {activity.type}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{activity.date}</div>
                        {activity.summary && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{activity.summary}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {activities.length > 5 && (
                    <Button variant="ghost" size="sm" onClick={() => navigate('/activities')}>
                      Ver todas las actividades →
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* Company Card */}
          {company && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-medium tracking-tight">Empresa</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                  onClick={() => navigate(`/companies/${company.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-8 h-8 text-primary" />
                    <div>
                      <div className="font-medium">{company.name}</div>
                      <div className="text-xs text-muted-foreground">{company.industry}</div>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {company.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Opportunities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium tracking-tight">
                Oportunidades ({opportunities.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {opportunities.length === 0 ? (
                <p className="text-sm text-muted-foreground">No hay oportunidades relacionadas</p>
              ) : (
                <div className="space-y-2">
                  {opportunities.slice(0, 3).map((opp) => (
                    <div 
                      key={opp.id}
                      className="p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => navigate('/pipeline')}
                    >
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{opp.name}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{opp.stage}</Badge>
                            {opp.value && (
                              <span className="text-xs text-muted-foreground">
                                €{opp.value.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {opportunities.length > 3 && (
                    <Button variant="ghost" size="sm" onClick={() => navigate('/pipeline')}>
                      Ver más →
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-medium tracking-tight">Metadatos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Creado</div>
                <div>{contact.created_at ? new Date(contact.created_at).toLocaleString() : 'N/A'}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ContactDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        contact={contact}
        onSuccess={fetchContactData}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Eliminar Contacto"
        description={`¿Estás seguro de que quieres eliminar a "${getFullName(contact)}"? Esta acción no se puede deshacer.`}
      />
    </div>
  );
};

export default ContactDetail;
